import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { downloadFile } from '../api/client'
import {
  useInstruments,
  useProjectSelfChecks,
  useSelfCheck,
  useSelfCheckAction,
  useSelfCheckTemplate
} from '../api/queries'
import type { SelfCheck, SelfCheckCircuit, SelfCheckResult } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { formatDate, formatDateTime } from '../lib/format'
import { useModuleEnabled } from '../navigation'
import { Card, EmptyState, ErrorBox, Loading } from './ui'

/**
 * Egenkontrol på sagen (docs/DECISIONS.md beslutning 131) - STØTTE til husets KLS-system.
 *
 * Montøren udfylder den visuelle kontrol og målingerne pr. gruppe på telefonen. Når den afsluttes, lægges en PDF i sagens
 * Dokumenter-mappe i SharePoint, så den kan vedhæftes i KLS-systemet. Systemet vurderer ALDRIG måleværdierne.
 */

const errorText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback)

/** "0,5" og "0.5" er det samme - montøren taster, som han er vant til. */
function parseNum(text: string): number | null {
  const t = text.trim().replace(',', '.')
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : NaN
}

function showNum(n?: number | null) {
  return n === null || n === undefined ? '' : String(n).replace('.', ',')
}

/**
 * Advarslen, når en sag afsluttes uden en afsluttet egenkontrol. Null = ingen advarsel (funktionen er fra, det er en
 * servicesag, eller der er en). Spærrer ALDRIG - det er brugerens valg.
 */
export function useSelfCheckWarning(projectId: number): string | null {
  const on = useModuleEnabled()('self_checks')
  const { data } = useProjectSelfChecks(projectId, on)
  if (!on || !data || !data.expected || data.hasCompleted) return null
  return 'Der er ingen afsluttet egenkontrol på sagen. Afslut sagen alligevel?'
}

// ---------------------------------------------------------------------------
// Kortet på sagen
// ---------------------------------------------------------------------------

export function SelfChecksCard({ projectId }: { projectId: number }) {
  const on = useModuleEnabled()('self_checks')
  const { data, isLoading, error } = useProjectSelfChecks(projectId, on)
  const action = useSelfCheckAction()
  const navigate = useNavigate()
  const { isIntranetAdmin } = useAuth()
  const [editingTemplate, setEditingTemplate] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  if (!on || isLoading || error || !data || !data.expected) return null

  async function create() {
    setActionError(null)
    try {
      const check = await action.mutateAsync({ path: `/api/projects/${projectId}/self-checks`, body: {} })
      navigate(`/self-checks/${check.id}`)
    } catch (err) {
      setActionError(errorText(err, 'Egenkontrollen kunne ikke oprettes.'))
    }
  }

  async function retry(id: number) {
    setActionError(null)
    try {
      await action.mutateAsync({ path: `/api/self-checks/${id}/upload` })
    } catch (err) {
      setActionError(errorText(err, 'Det lykkedes ikke.'))
    }
  }

  return (
    <Card
      title={`Egenkontrol${data.hasCompleted ? ' ✓' : ''}`}
      actions={
        <button className="btn btn--sm btn--primary" disabled={action.isPending} onClick={() => void create()}>
          + Ny egenkontrol
        </button>
      }
    >
      <div className="stack" style={{ gap: 8 }}>
        {data.checks.length === 0 && (
          <EmptyState>Ingen egenkontrol endnu. Udfyld den, når installationen er færdig - PDF'en lægges i sagens mappe.</EmptyState>
        )}
        {data.checks.map((c) => (
          <div key={c.id} className="row row--between" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Link to={`/self-checks/${c.id}`} style={{ minWidth: 0 }}>
              <div className="small">
                <strong>{c.title}</strong>
              </div>
              <div className="tiny muted">
                {c.completedAt
                  ? `Afsluttet ${formatDate(c.completedAt)}${c.completedBy ? ` af ${c.completedBy.displayName}` : ''}`
                  : `Kladde · oprettet ${formatDate(c.createdAt)}`}
              </div>
            </Link>
            <div className="row" style={{ gap: 6 }}>
              {c.pdfWebUrl && (
                <a className="btn btn--sm" href={c.pdfWebUrl} target="_blank" rel="noreferrer">
                  PDF i SharePoint
                </a>
              )}
              {c.completedAt && !c.hasPdf && (
                <button className="btn btn--sm" onClick={() => void retry(c.id)} title={c.uploadError ?? undefined}>
                  Læg i SharePoint igen
                </button>
              )}
            </div>
          </div>
        ))}
        {actionError && <div className="error-box">{actionError}</div>}
        {isIntranetAdmin && (
          <div>
            <button className="btn btn--sm" onClick={() => setEditingTemplate((v) => !v)}>
              {editingTemplate ? 'Luk' : 'Ret punkterne til den visuelle kontrol'}
            </button>
          </div>
        )}
        {editingTemplate && <TemplateEditor />}
      </div>
    </Card>
  )
}

/** Kontoret retter husets liste. Kun nye egenkontroller får ændringen. */
function TemplateEditor() {
  const { data, isLoading } = useSelfCheckTemplate(true)
  const action = useSelfCheckAction()
  const [draft, setDraft] = useState<{ key: string; id?: number; text: string; isActive: boolean }[] | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const rows = draft ?? (data ?? []).map((t) => ({ key: `t${t.id}`, id: t.id, text: t.text, isActive: t.isActive }))

  if (isLoading) return <Loading />

  async function save() {
    setMessage(null)
    try {
      await action.mutateAsync({
        path: '/api/self-checks/template-items',
        method: 'put',
        body: { items: rows.filter((r) => r.text.trim()).map((r) => ({ id: r.id, text: r.text.trim(), isActive: r.isActive })) }
      })
      setDraft(null)
      setMessage('Gemt. Kun nye egenkontroller får ændringen.')
    } catch (err) {
      setMessage(errorText(err, 'Listen kunne ikke gemmes.'))
    }
  }

  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="tiny muted">Listen er et EKSEMPEL - ret den efter jeres KLS.</div>
      {rows.map((r) => (
        <div key={r.key} className="row" style={{ gap: 6, alignItems: 'center' }}>
          <input value={r.text} maxLength={300} onChange={(e) => setDraft(rows.map((x) => (x.key === r.key ? { ...x, text: e.target.value } : x)))} />
          <label className="tiny row" style={{ gap: 4 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={r.isActive} onChange={(e) => setDraft(rows.map((x) => (x.key === r.key ? { ...x, isActive: e.target.checked } : x)))} />
            Med
          </label>
          <button className="btn btn--sm" aria-label="Fjern punktet" onClick={() => setDraft(rows.filter((x) => x.key !== r.key))}>
            ✕
          </button>
        </div>
      ))}
      <div className="row" style={{ gap: 6 }}>
        <button className="btn btn--sm" onClick={() => setDraft([...rows, { key: `n${Date.now()}`, text: '', isActive: true }])}>
          + Punkt
        </button>
        <button className="btn btn--sm btn--primary" disabled={!draft || action.isPending} onClick={() => void save()}>
          Gem listen
        </button>
      </div>
      {message && <div className="tiny">{message}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Én egenkontrol
// ---------------------------------------------------------------------------

export function SelfCheckPage() {
  const id = Number(useParams().id)
  const { data, isLoading, error } = useSelfCheck(id)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return <EmptyState>Egenkontrollen findes ikke.</EmptyState>

  // Formularen bygges forfra, naar egenkontrollen skifter (fx efter afslutning).
  return <SelfCheckForm key={`${data.id}-${data.completedAt ?? 'kladde'}`} check={data} />
}

interface CircuitDraft {
  key: string
  name: string
  protection: string
  continuity: string
  insulation: string
  rcdMa: string
  rcdMs: string
  zs: string
  isOk: boolean
  note: string
}

const RESULTS: { value: SelfCheckResult; label: string }[] = [
  { value: 'ok', label: 'I orden' },
  { value: 'not_ok', label: 'Ikke i orden' },
  { value: 'na', label: 'Ikke relevant' }
]

function toDraft(c: SelfCheckCircuit, i: number): CircuitDraft {
  return {
    key: `c${c.id ?? i}`,
    name: c.name,
    protection: c.protection ?? '',
    continuity: showNum(c.continuityOhm),
    insulation: showNum(c.insulationMOhm),
    rcdMa: showNum(c.rcdRatedMa),
    rcdMs: showNum(c.rcdTripMs),
    zs: showNum(c.loopImpedanceOhm),
    isOk: c.isOk,
    note: c.note ?? ''
  }
}

function SelfCheckForm({ check }: { check: SelfCheck }) {
  const navigate = useNavigate()
  const action = useSelfCheckAction()
  const { data: instruments } = useInstruments()
  const locked = !check.canEdit

  const [title, setTitle] = useState(check.title)
  const [notes, setNotes] = useState(check.notes ?? '')
  const [instrumentId, setInstrumentId] = useState(check.instrumentId ? String(check.instrumentId) : '')
  const [items, setItems] = useState(() => check.items.map((i) => ({ ...i })))
  const [circuits, setCircuits] = useState<CircuitDraft[]>(() =>
    check.circuits.length > 0 ? check.circuits.map(toDraft) : [toDraft({ name: '', isOk: true }, 0)]
  )
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const activeInstruments = (instruments ?? []).filter((i) => i.isActive)
  const chosen = activeInstruments.find((i) => String(i.id) === instrumentId)
  const chosenExpired =
    chosen?.calibrationIntervalMonths && chosen.nextCalibrationDate && new Date(chosen.nextCalibrationDate) < new Date()

  function body() {
    const bad = circuits.some((c) => [c.continuity, c.insulation, c.rcdMa, c.rcdMs, c.zs].some((v) => Number.isNaN(parseNum(v))))
    if (bad) throw new Error('En måleværdi er ikke et tal.')
    return {
      title: title.trim() || null,
      notes: notes.trim() || null,
      instrumentId: instrumentId ? Number(instrumentId) : null,
      items: items.map((i) => ({ id: i.id, result: i.result ?? null, note: i.note?.trim() || null })),
      circuits: circuits
        .filter((c) => c.name.trim() || c.continuity || c.insulation || c.rcdMs || c.zs)
        .map((c) => ({
          name: c.name.trim(),
          protection: c.protection.trim() || null,
          continuityOhm: parseNum(c.continuity),
          insulationMOhm: parseNum(c.insulation),
          rcdRatedMa: parseNum(c.rcdMa),
          rcdTripMs: parseNum(c.rcdMs),
          loopImpedanceOhm: parseNum(c.zs),
          isOk: c.isOk,
          note: c.note.trim() || null
        }))
    }
  }

  async function save(complete: boolean) {
    setError(null)
    setMessage(null)
    try {
      const saved = await action.mutateAsync({ path: `/api/self-checks/${check.id}`, method: 'put', body: body() })
      if (!complete) {
        setMessage(saved.whyNotCompletable ? `Gemt. Mangler før den kan afsluttes: ${saved.whyNotCompletable}` : 'Gemt - klar til at afslutte.')
        return
      }
      if (saved.whyNotCompletable) {
        setError(saved.whyNotCompletable)
        return
      }
      if (!window.confirm(`Afslut egenkontrollen? Den låses, og PDF'en lægges i sagens mappe i SharePoint.`)) return
      const done = await action.mutateAsync({ path: `/api/self-checks/${check.id}/complete` })
      if (done.uploadError) setError(`Afsluttet - men PDF'en kom ikke i SharePoint: ${done.uploadError}`)
    } catch (err) {
      setError(errorText(err, 'Det kunne ikke gemmes.'))
    }
  }

  async function remove() {
    if (!window.confirm('Slet kladden?')) return
    try {
      await action.mutateAsync({ path: `/api/self-checks/${check.id}`, method: 'delete' })
      navigate(`/projects/${check.projectId}`)
    } catch (err) {
      setError(errorText(err, 'Kladden kunne ikke slettes.'))
    }
  }

  function setCircuit(key: string, patch: Partial<CircuitDraft>) {
    setCircuits((all) => all.map((c) => (c.key === key ? { ...c, ...patch } : c)))
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          ✅ Egenkontrol
        </h1>
        <Link className="btn btn--sm" to={`/projects/${check.projectId}`}>
          Til sagen
        </Link>
      </div>

      <Card>
        <div className="stack" style={{ gap: 8 }}>
          <div className="small">
            Sag: <Link to={`/projects/${check.projectId}`}>{check.projectName}</Link>
          </div>
          {locked ? (
            <div className="small">
              <strong>{check.title}</strong> · afsluttet {formatDateTime(check.completedAt)}
              {check.completedBy ? ` af ${check.completedBy.displayName}` : ''}
            </div>
          ) : (
            <label className="small">
              Hvad dækker den?
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Fx køkken og bad" />
            </label>
          )}
          {locked ? (
            <div className="small">
              Måleinstrument: {check.instrumentName ?? '-'}
              {check.instrumentSerialNumber ? ` (${check.instrumentSerialNumber})` : ''}
            </div>
          ) : (
            <label className="small">
              Måleinstrument
              <select value={instrumentId} onChange={(e) => setInstrumentId(e.target.value)}>
                <option value="">Vælg ...</option>
                {activeInstruments.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                    {i.serialNumber ? ` (${i.serialNumber})` : ''}
                  </option>
                ))}
              </select>
              {chosenExpired && <span className="tiny warn">Kalibreringen er udløbet - brug et andet instrument.</span>}
            </label>
          )}
          {check.pdfWebUrl && (
            <a className="btn btn--sm" href={check.pdfWebUrl} target="_blank" rel="noreferrer" style={{ alignSelf: 'flex-start' }}>
              PDF i SharePoint
            </a>
          )}
          {check.uploadError && <div className="tiny warn">PDF'en kom ikke i SharePoint: {check.uploadError}</div>}
        </div>
      </Card>

      <Card title="Visuel kontrol">
        <div className="stack" style={{ gap: 10 }}>
          {items.map((item, idx) => (
            <div key={item.id} className="stack" style={{ gap: 4 }}>
              <div className="small">
                {idx + 1}. {item.text}
              </div>
              <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                {RESULTS.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    disabled={locked}
                    className={`btn btn--sm ${item.result === r.value ? (r.value === 'not_ok' ? 'btn--danger' : 'btn--primary') : ''}`}
                    onClick={() => setItems((all) => all.map((x) => (x.id === item.id ? { ...x, result: r.value } : x)))}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              {(item.result === 'not_ok' || item.note) &&
                (locked ? (
                  <div className="tiny">📝 {item.note}</div>
                ) : (
                  <input
                    value={item.note ?? ''}
                    maxLength={500}
                    placeholder="Hvad er ikke i orden, og hvad gøres der ved det?"
                    onChange={(e) => setItems((all) => all.map((x) => (x.id === item.id ? { ...x, note: e.target.value } : x)))}
                  />
                ))}
            </div>
          ))}
        </div>
      </Card>

      <Card title="Målinger pr. gruppe">
        <div className="stack" style={{ gap: 12 }}>
          <div className="tiny muted">
            Skriv værdierne, som instrumentet viser dem. Systemet vurderer dem ikke - sæt flueben, når gruppen er i orden.
          </div>
          {circuits.map((c) => (
            <div key={c.key} className="stack" style={{ gap: 6, padding: 10, border: '1px solid var(--border, #E2E8F0)', borderRadius: 8 }}>
              <div className="row" style={{ gap: 6 }}>
                <input disabled={locked} value={c.name} onChange={(e) => setCircuit(c.key, { name: e.target.value })} placeholder="Gruppe, fx Gr. 3 - stik køkken" maxLength={100} style={{ flex: '1 1 200px' }} />
                <input disabled={locked} value={c.protection} onChange={(e) => setCircuit(c.key, { protection: e.target.value })} placeholder="B13" maxLength={30} style={{ width: 70 }} aria-label="Beskyttelse" />
              </div>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <Measure label="PE (Ω)" value={c.continuity} disabled={locked} onChange={(v) => setCircuit(c.key, { continuity: v })} />
                <Measure label="Iso (MΩ)" value={c.insulation} disabled={locked} onChange={(v) => setCircuit(c.key, { insulation: v })} />
                <Measure label="RCD mA" value={c.rcdMa} disabled={locked} onChange={(v) => setCircuit(c.key, { rcdMa: v })} />
                <Measure label="RCD ms" value={c.rcdMs} disabled={locked} onChange={(v) => setCircuit(c.key, { rcdMs: v })} />
                <Measure label="Zs (Ω)" value={c.zs} disabled={locked} onChange={(v) => setCircuit(c.key, { zs: v })} />
              </div>
              <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <label className="small row" style={{ gap: 6 }}>
                  <input type="checkbox" style={{ width: 'auto' }} disabled={locked} checked={c.isOk} onChange={(e) => setCircuit(c.key, { isOk: e.target.checked })} />
                  Gruppen er i orden
                </label>
                {(!c.isOk || c.note) && (
                  <input disabled={locked} value={c.note} onChange={(e) => setCircuit(c.key, { note: e.target.value })} placeholder="Note" maxLength={300} style={{ flex: '1 1 180px' }} />
                )}
                {!locked && (
                  <button className="btn btn--sm" onClick={() => setCircuits((all) => all.filter((x) => x.key !== c.key))}>
                    Fjern
                  </button>
                )}
              </div>
            </div>
          ))}
          {!locked && (
            <div>
              <button className="btn btn--sm" onClick={() => setCircuits((all) => [...all, toDraft({ name: '', isOk: true }, Date.now())])}>
                + Gruppe
              </button>
            </div>
          )}
        </div>
      </Card>

      <Card title="Bemærkninger">
        {locked ? (
          <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{check.notes || '-'}</div>
        ) : (
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={2000} />
        )}
      </Card>

      {message && <div className="map-notice">{message}</div>}
      {error && <div className="error-box">{error}</div>}

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {!locked && (
          <>
            <button className="btn" disabled={action.isPending} onClick={() => void save(false)}>
              Gem
            </button>
            <button className="btn btn--primary" disabled={action.isPending} onClick={() => void save(true)}>
              Afslut og læg i SharePoint
            </button>
          </>
        )}
        <button
          className="btn"
          onClick={() => void downloadFile(`/api/self-checks/${check.id}/pdf`, 'Egenkontrol.pdf').catch((err) => setError(errorText(err, `PDF'en kunne ikke hentes.`)))}
        >
          Hent PDF
        </button>
        {check.canDelete && (
          <button className="btn btn--danger" onClick={() => void remove()}>
            Slet kladden
          </button>
        )}
      </div>
    </div>
  )
}

function Measure({ label, value, disabled, onChange }: { label: string; value: string; disabled: boolean; onChange: (v: string) => void }) {
  return (
    <label className="tiny" style={{ width: 82 }}>
      {label}
      <input inputMode="decimal" disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  )
}
